import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import { styled } from '@mui/system';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';

interface Props {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: Props) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const hasTriggeredRedirectRef = useRef(false);

  useEffect(() => {
    if (
      isLoading ||
      isAuthenticated ||
      error ||
      hasTriggeredRedirectRef.current
    ) {
      return;
    }

    hasTriggeredRedirectRef.current = true;
    void loginWithRedirect({
      appState: { returnTo: `${location.pathname}${location.search}` },
    }).catch((error) => {
      Sentry.captureException(error, {
        tags: { feature: 'auth0_login_redirect' },
      });
      hasTriggeredRedirectRef.current = false;
    });
  }, [
    isLoading,
    isAuthenticated,
    error,
    loginWithRedirect,
    location.pathname,
    location.search,
  ]);

  if (isLoading) {
    return (
      <LoadingWrapper>
        <CircularProgress />
      </LoadingWrapper>
    );
  }

  if (error) {
    return (
      <LoadingWrapper role="alert">
        Unable to sign in. Please refresh and try again.
      </LoadingWrapper>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children;
};

export { ProtectedRoute };

const LoadingWrapper = styled(Stack)({
  height: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
});
